import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { API_URL } from './adminUtils';
import {
  CheckCircle2,
  Download,
  Edit,
  Save,
  Loader2,
  FileText,
} from 'lucide-react';

const ConstitutionEditor = () => {
  const [constitution, setConstitution] = useState(null);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('en');
  const [formData, setFormData] = useState({ title: '', content: '', content_fr: '', version: '' });
  const [saving, setSaving] = useState(false);
  const [compiling, setCompiling] = useState(false);
  const [MDEditor, setMDEditor] = useState(null);
  const [signatureStatus, setSignatureStatus] = useState(null);
  
  // Dynamic import for MDEditor
  useEffect(() => {
    import('@uiw/react-md-editor').then(module => {
      setMDEditor(() => module.default);
    });
  }, []);
  
  const fetchConstitution = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/constitution`);
      setConstitution(response.data);
      setFormData({
        title: response.data.title,
        content: response.data.content,
        content_fr: response.data.content_fr || '',
        version: response.data.version
      });
    } catch (error) {
    }
  }, []);
  
  const fetchSignatureStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/signatures/status`);
      setSignatureStatus(response.data);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    fetchConstitution();
    fetchSignatureStatus();
  }, [fetchConstitution, fetchSignatureStatus]);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      
      if (constitution) {
        await axios.put(`${API_URL}/constitution/${constitution.id}`, formData);
      } else {
        await axios.post(`${API_URL}/constitution`, formData);
      }
      toast.success('Constitution saved successfully');
      setEditing(false);
      fetchConstitution();
    } catch (error) {
      toast.error('Failed to save constitution: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };
  
  const handleCompile = async () => {
    if (!constitution) return;
    
    const confirmMsg = `Are you sure you want to compile this constitution?\n\nThis will:\n- Lock the constitution (no more signatures)\n- Sync all association names on existing signatures\n- Make the PDF available for public download\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMsg)) return;
    
    setCompiling(true);
    try {
      
      const response = await axios.post(`${API_URL}/constitution/${constitution.id}/compile`, {});
      toast.success(`Constitution compiled! ${response.data.signatures_synced} signature names synced.`);
      fetchConstitution();
      fetchSignatureStatus();
    } catch (error) {
      toast.error('Failed to compile: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCompiling(false);
    }
  };
  
  const handleTogglePublicDownload = async (enabled) => {
    if (!constitution) return;
    
    try {
      
      await axios.put(`${API_URL}/constitution/${constitution.id}/public-download?enabled=${enabled}`, {});
      toast.success(`Public download ${enabled ? 'enabled' : 'disabled'}`);
      fetchConstitution();
      fetchSignatureStatus();
    } catch (error) {
      toast.error('Failed to toggle public download: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/constitution/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CCA_Constitution_${constitution?.version || '1.0'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold">Constitution Editor</h1>
          <p className="text-muted-foreground">Manage the CCA constitution with rich text editing</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {constitution?.is_compiled ? (
            <>
              <Badge className="bg-cameroon-green text-white">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Compiled
              </Badge>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleTogglePublicDownload(!signatureStatus?.public_download_enabled)}
              >
                {signatureStatus?.public_download_enabled ? 'Disable Public Download' : 'Enable Public Download'}
              </Button>
            </>
          ) : (
            <Button 
              onClick={handleCompile} 
              disabled={compiling}
              className="bg-cameroon-red hover:bg-cameroon-red/90"
              data-testid="compile-constitution-btn"
            >
              {compiling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Compiling...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Compile Constitution
                </>
              )}
            </Button>
          )}
          <Button variant="outline" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
          {!editing && !constitution?.is_compiled && (
            <Button onClick={() => setEditing(true)} data-testid="edit-constitution-btn">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>
      
      {/* Compilation Status Card */}
      {constitution?.is_compiled && (
        <Card className="border-cameroon-green/30 bg-cameroon-green/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-cameroon-green/20 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-cameroon-green" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-cameroon-green">Constitution Compiled</p>
                <p className="text-sm text-muted-foreground">
                  Compiled on {constitution.compiled_at ? new Date(constitution.compiled_at).toLocaleDateString() : 'N/A'} • 
                  {signatureStatus?.signed || 0} signatures • 
                  Public download: {signatureStatus?.public_download_enabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <Card>
        <CardContent className="p-6">
          {editing ? (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    data-testid="constitution-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Version</Label>
                  <Input
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    data-testid="constitution-version"
                  />
                </div>
              </div>
              
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                  <TabsTrigger value="en">English Content</TabsTrigger>
                  <TabsTrigger value="fr">French Content</TabsTrigger>
                </TabsList>
                <TabsContent value="en" className="space-y-2">
                  <Label>English Content (Markdown supported)</Label>
                  {MDEditor ? (
                    <div data-color-mode="light">
                      <MDEditor
                        value={formData.content}
                        onChange={(value) => setFormData({ ...formData, content: value || '' })}
                        height={400}
                        preview="edit"
                      />
                    </div>
                  ) : (
                    <Textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={20}
                      className="font-mono text-sm"
                      placeholder="Loading editor..."
                    />
                  )}
                </TabsContent>
                <TabsContent value="fr" className="space-y-2">
                  <Label>French Content / Contenu en Français (Markdown supported)</Label>
                  {MDEditor ? (
                    <div data-color-mode="light">
                      <MDEditor
                        value={formData.content_fr}
                        onChange={(value) => setFormData({ ...formData, content_fr: value || '' })}
                        height={400}
                        preview="edit"
                      />
                    </div>
                  ) : (
                    <Textarea
                      value={formData.content_fr}
                      onChange={(e) => setFormData({ ...formData, content_fr: e.target.value })}
                      rows={20}
                      className="font-mono text-sm"
                      placeholder="Loading editor..."
                    />
                  )}
                </TabsContent>
              </Tabs>
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-cameroon-green hover:bg-cameroon-green/90" data-testid="save-constitution-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-6 pb-6 border-b">
                <div>
                  <h2 className="font-serif text-xl font-bold">{constitution?.title}</h2>
                  <p className="text-sm text-muted-foreground">Version {constitution?.version}</p>
                </div>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList>
                    <TabsTrigger value="en">English</TabsTrigger>
                    <TabsTrigger value="fr">Français</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <ScrollArea className="h-[60vh]">
                <div className="prose prose-sm max-w-none pr-4 whitespace-pre-wrap">
                  {activeTab === 'en' ? constitution?.content : constitution?.content_fr}
                </div>
              </ScrollArea>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConstitutionEditor;
